import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { apiErrorMessage } from '../../shared/api/errors';
import { Button } from '../../shared/ui/Button';
import { ErrorText, Spinner } from '../../shared/ui/primitives';
import { getFileContent } from './api';
import type { ExaminationFile } from './types';

/** Просмотр текстового вложения (txt/xml/html) без ухода со страницы. */
export function DocPreviewModal({ file, onClose }: { file: ExaminationFile; onClose: () => void }) {
  const isHtml = /\.html?$/i.test(file.originalName || file.title);
  // Для html по умолчанию показываем рендер (в песочнице), с возможностью
  // переключиться на исходный код. Для txt/xml — только текст.
  const [showSource, setShowSource] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['file-content', file.id],
    queryFn: () => getFileContent(file.id),
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const renderHtml = isHtml && !showSource;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Просмотр файла ${file.originalName || file.title}`}
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-line px-4 py-3">
          <p className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
            {file.originalName || file.title}
          </p>
          {isHtml && !isLoading && !isError && (
            <button
              type="button"
              className="shrink-0 text-xs font-medium text-accent hover:text-accent-600"
              onClick={() => setShowSource((v) => !v)}
            >
              {showSource ? 'Показать разметку' : 'Показать как текст'}
            </button>
          )}
          <button
            type="button"
            className="shrink-0 rounded-md p-1 text-muted hover:bg-canvas hover:text-ink"
            aria-label="Закрыть"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto p-4">
          {isLoading && <Spinner />}
          {isError && (
            <ErrorText>{apiErrorMessage(error, 'Не удалось загрузить содержимое файла')}</ErrorText>
          )}
          {!isLoading && !isError && renderHtml && (
            <iframe
              title={`Просмотр ${file.originalName || file.title}`}
              sandbox=""
              srcDoc={data ?? ''}
              className="h-[60vh] w-full rounded-lg border border-line bg-white"
            />
          )}
          {!isLoading && !isError && !renderHtml && (
            <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-ink">
              {data}
            </pre>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-line px-4 py-3">
          <a href={file.downloadUrl} target="_blank" rel="noreferrer">
            <Button type="button" size="sm" variant="secondary">
              Скачать
            </Button>
          </a>
          <Button type="button" size="sm" onClick={onClose}>
            Закрыть
          </Button>
        </div>
      </div>
    </div>
  );
}
